import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';

import { Chip } from '../ds';
import type { MyPlanCardDto } from '../../lib/api/openingTasks';
import { useViewAsReadOnly } from '../opening-tasks/useViewAsReadOnly';
import {
  countTasks,
  emptyTasksText,
  groupPlanTasks,
  isPlanReadOnly,
  resolveStageFilter,
  type PlanMode,
  type SideFilter,
  type StatusFilter,
  type TaskFilters,
} from './plan-model';
import { doableThisWeekIds } from './plan-gantt-model';
import { PlanTaskGroups, SideChips } from './PlanTaskGroups';

const STATUS_LABEL: Record<StatusFilter, string> = { open: 'Открытые', overdue: 'Просроченные', done: 'Выполненные', all: 'Все' };
const STATUS_ORDER: StatusFilter[] = ['open', 'overdue', 'done', 'all'];

/**
 * Вкладка «Задачи»: все задачи плана по этапам. Фильтры-чипы: чьи («Мои» — по умолчанию, «УК», «Все»), статус
 * («Открытые» — по умолчанию), этап — из адреса (`?stage=`, снимается чипом «Этап: … ✕»). Отметки — те же, что в
 * «Моих задачах»; задачи УК партнёр только смотрит. У менеджера (`mode="manager"`) отметок нет совсем.
 */
export function PlanTasksTab({
  card,
  stageParam,
  onClearStage,
  mode = 'partner',
}: {
  card: MyPlanCardDto;
  stageParam: string | null;
  onClearStage: () => void;
  mode?: PlanMode;
}): JSX.Element {
  const readOnly = isPlanReadOnly(useViewAsReadOnly(), mode);
  const stage = resolveStageFilter(stageParam, card.stages);
  const stageDef = stage ? card.stages.find((s) => s.code === stage) : undefined;
  const [side, setSide] = useState<SideFilter>('mine');
  // Пришли с пройденного этапа — «Открытых» там нет, сразу показываем всё.
  const [status, setStatus] = useState<StatusFilter>(() => (stageDef?.state === 'done' ? 'all' : 'open'));
  const filters: TaskFilters = { side, status, stage };

  const groups = useMemo(() => groupPlanTasks(card.stages, card.tasks, { side, status, stage }), [card.stages, card.tasks, side, status, stage]);
  const doableIds = useMemo(() => (mode === 'partner' ? doableThisWeekIds(card.tasks, card.today) : undefined), [card.tasks, card.today, mode]);
  const sideCounts: Record<SideFilter, number> = {
    mine: countTasks(card.tasks, filters, { side: 'mine' }),
    uk: countTasks(card.tasks, filters, { side: 'uk' }),
    all: countTasks(card.tasks, filters, { side: 'all' }),
  };

  return (
    <div className="fk-op-panel">
      <div className="fk-op-filters">
        <SideChips value={side} onChange={setSide} counts={sideCounts} mode={mode} />
        <div className="fk-op-frow" role="group" aria-label="Статус задач">
          <span className="fk-op-flabel">Статус</span>
          {STATUS_ORDER.map((k) => (
            <Chip key={k} active={status === k} count={countTasks(card.tasks, filters, { status: k })} onClick={() => setStatus(k)}>
              {STATUS_LABEL[k]}
            </Chip>
          ))}
        </div>
        {stageDef ? (
          <div className="fk-op-frow">
            <span className="fk-op-flabel">Этап</span>
            <Chip active icon={<CloseOutlined aria-hidden="true" />} title="Показать задачи всех этапов" onClick={onClearStage}>
              {`Этап: ${stageDef.label}`}
            </Chip>
          </div>
        ) : null}
      </div>

      {groups.length === 0 ? (
        <div className="fk-op-card">
          <div className="fk-ot-empty">{emptyTasksText(filters, card.tasks.length, countTasks(card.tasks, filters, { status: 'all' }), mode)}</div>
        </div>
      ) : (
        <PlanTaskGroups groups={groups} readOnly={readOnly} doableIds={doableIds} />
      )}
    </div>
  );
}
